import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiService from '../../services/api-service';
import notifier from '../Notifier';
import { getErrorString, HMS } from '../../constants/function';
import { Images } from '../../assets/common';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { hostDayLabel, todaysDay, UNSCHEDULED_DAY } from './jukeboxSchedule';

const ALL_DAYS = 'all';

export default function SongRequestList({ open, onClose, roomId, onAcceptSong }) {
  const { getCurrentBranding } = useAppTheme();
  const brandName = getCurrentBranding()?.appName || 'SIKI';
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  // The host's jukebox days, to filter the queue by the day fans picked.
  const [days, setDays] = useState([]);
  const [dayFilter, setDayFilter] = useState(ALL_DAYS);

  useEffect(() => {
    if (open && roomId) {
      fetchRequests();
    }
  }, [open, roomId]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    ApiService.SONG_REQUEST_HOST_DAYS()
      .then((res) => {
        if (active) setDays(Array.isArray(res?.result?.days) ? res.result.days : []);
      })
      // The filter is optional; without days the list just shows everything.
      .catch(() => active && setDays([]));
    return () => {
      active = false;
    };
  }, [open]);

  const today = todaysDay(days);
  const visibleRequests = requests.filter((request) => {
    if (dayFilter === ALL_DAYS) return true;
    if (dayFilter === UNSCHEDULED_DAY) return !request.slotId;
    return request.slotId === dayFilter;
  });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await ApiService.SONG_REQUEST_LIST(roomId);
      if (response.success) {
        setRequests(response.result || []);
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (request) => {
    setProcessingId(request._id);
    try {
      const response = await ApiService.SONG_REQUEST_ACCEPT(request._id);
      if (response.success) {
        notifier.success(`Song request accepted! You received ${request.tipAmount} SIKI`);

        // Remove the accepted request from the list
        setRequests(requests.filter(r => r._id !== request._id));

        // Pass the song to the parent component to add to playlist
        if (onAcceptSong && response.result.song) {
          onAcceptSong(response.result.song);
        }
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (request) => {
    setProcessingId(request._id);
    try {
      const response = await ApiService.SONG_REQUEST_REJECT(request._id);
      if (response.success) {
        notifier.success(`Song request rejected - ${request.tipAmount} SIKI refunded to requester`);

        // Remove the rejected request from the list
        setRequests(requests.filter(r => r._id !== request._id));
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleClearProcessed = async () => {
    try {
      const response = await ApiService.SONG_REQUEST_CLEAR(roomId);
      if (response.success) {
        notifier.success('Cleared processed requests');
      }
    } catch (error) {
      notifier.error(getErrorString(error));
    }
  };

  const iconButtonSx = {
    color: 'rgba(255, 255, 255, 0.5)',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.06)' },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '80vh',
          m: 2,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--surface, #0C0C12)',
          backgroundImage: 'none',
          color: '#fff',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>Song requests</Typography>
            {requests.length > 0 && (
              <Box component="span" sx={{
                px: 1, py: '1px', borderRadius: '999px', fontSize: 12, fontWeight: 700,
                fontFamily: 'var(--font-mono)', color: 'var(--accent, #FFEE20)',
                backgroundColor: 'rgba(255, 238, 32, 0.1)',
              }}>
                {requests.length}
              </Box>
            )}
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: 'var(--text-muted, #6A6A80)' }}>
            Accept to queue the song and keep the tip. Declined tips go back to the fan.
          </Typography>
        </Box>
        <IconButton onClick={fetchRequests} size="small" disabled={loading} title="Refresh" sx={iconButtonSx}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={onClose} size="small" title="Close" sx={iconButtonSx}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Day filter: which jukebox day the requests were made for. */}
      {days.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 3, py: 1.25, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <TextField
            select
            size="small"
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': 'Filter by day' }}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': { color: '#fff', fontSize: 13, borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.04)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
              '& select option': { background: '#0C0C12', color: '#fff' },
            }}
          >
            <option value={ALL_DAYS}>All days</option>
            {days.map((day) => (
              <option key={day.id} value={day.id}>
                {hostDayLabel(day)}
                {day.description ? ` — ${day.description}` : ''}
              </option>
            ))}
            <option value={UNSCHEDULED_DAY}>Unscheduled / live</option>
          </TextField>
          {today && (
            <Button
              onClick={() => setDayFilter(today.id)}
              size="small"
              sx={{
                flexShrink: 0, minWidth: 0, px: 1.5, height: 32, borderRadius: '8px', textTransform: 'none',
                fontSize: 12, fontWeight: 600,
                color: dayFilter === today.id ? '#000' : 'var(--accent, #FFEE20)',
                backgroundColor: dayFilter === today.id ? 'var(--accent, #FFEE20)' : 'transparent',
                border: '1px solid rgba(255, 238, 32, 0.4)',
                '&:hover': { backgroundColor: dayFilter === today.id ? 'var(--accent-bright, #FFF56B)' : 'rgba(255, 238, 32, 0.08)' },
              }}
            >
              Today
            </Button>
          )}
        </Stack>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={24} sx={{ color: 'var(--accent, #FFEE20)' }} />
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <QueueMusicIcon sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.2)' }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: '#fff' }}>No requests yet</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 12, color: 'var(--text-muted, #6A6A80)' }}>
              Viewers request songs from the Jukebox button.
            </Typography>
          </Box>
        ) : visibleRequests.length === 0 ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>No requests for this day</Typography>
            <Button
              onClick={() => setDayFilter(ALL_DAYS)}
              size="small"
              sx={{ mt: 1, textTransform: 'none', fontSize: 12, color: 'var(--accent, #FFEE20)' }}
            >
              Show all days
            </Button>
          </Box>
        ) : (
          visibleRequests.map((request) => {
            const song = request.songDetails || {};
            const requester = request.requesterId || {};
            const busy = processingId === request._id;
            return (
              <Box
                key={request._id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.75,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <Box
                  component="img"
                  src={song.thumbnailImg?.filePath || Images.default_music}
                  alt=""
                  sx={{ width: 48, height: 48, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                    {song.title || 'Untitled'}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: 'var(--text-muted, #6A6A80)' }}>
                    {[song.artist, song.duration ? HMS(song.duration) : null].filter(Boolean).join(' · ')}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5, minWidth: 0 }}>
                    <Box
                      component="img"
                      src={requester.profileImg?.filePath || Images.default_user_square}
                      alt=""
                      sx={{ width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <Typography noWrap sx={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {`${requester.firstName || ''} ${requester.lastName || ''}`.trim() || 'A fan'}
                    </Typography>
                  </Stack>
                </Box>

                <Typography sx={{ flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--accent, #FFEE20)' }}>
                  {request.tipAmount} {brandName}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    onClick={() => handleReject(request)}
                    disabled={busy}
                    size="small"
                    sx={{
                      minWidth: 0, px: 1.5, height: 32, borderRadius: '8px', textTransform: 'none',
                      fontSize: 13, fontWeight: 600, color: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      '&:hover': { color: '#FF3B5C', borderColor: 'rgba(255, 59, 92, 0.4)', backgroundColor: 'rgba(255, 59, 92, 0.08)' },
                    }}
                  >
                    Decline
                  </Button>
                  <Button
                    onClick={() => handleAccept(request)}
                    disabled={busy}
                    size="small"
                    startIcon={busy ? <CircularProgress size={14} sx={{ color: '#000' }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      minWidth: 0, px: 1.5, height: 32, borderRadius: '8px', textTransform: 'none',
                      fontSize: 13, fontWeight: 700, color: '#000',
                      backgroundColor: 'var(--accent, #FFEE20)',
                      '&:hover': { backgroundColor: 'var(--accent-bright, #FFF56B)' },
                      '&.Mui-disabled': { color: '#000', backgroundColor: 'rgba(255, 238, 32, 0.5)' },
                    }}
                  >
                    Accept
                  </Button>
                </Stack>
              </Box>
            );
          })
        )}
      </Box>

      {/* Footer */}
      {requests.length > 0 && !loading && (
        <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <Button
            onClick={handleClearProcessed}
            size="small"
            sx={{ textTransform: 'none', fontSize: 12, color: 'var(--text-muted, #6A6A80)', '&:hover': { color: '#fff', backgroundColor: 'transparent' } }}
          >
            Clear processed requests
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
